import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Star, Gift as GiftIcon } from "lucide-react";
import { trackInitiateCheckout } from "../lib/metaPixel";

const PRESETS = [50, 95, 145, 195];

export default function Gift() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "balance" ? "redeem" : "buy"); // buy | redeem
  const [result, setResult] = useState(null);
  const [amount, setAmount] = useState(95);
  const [customAmount, setCustomAmount] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const buy = async () => {
    if (!user || user === false) { nav("/login?next=/gift"); return; }
    const finalAmount = customAmount ? Number(customAmount) : amount;
    if (!finalAmount || finalAmount < 10) { toast.error("Minimum gift is $10"); return; }
    if (!recipientName || !recipientEmail) { toast.error("Add recipient name and email"); return; }
    setBusy(true);
    trackInitiateCheckout({ value: finalAmount, name: "Gift Voucher" });
    try {
      const { data: gift } = await api.post("/gift/purchase", {
        amount: finalAmount, recipient_name: recipientName,
        recipient_email: recipientEmail, message,
      });
      const { data: ck } = await api.post("/payments/checkout", {
        item_type: "gift", item_id: gift.id, origin_url: window.location.origin,
      });
      window.location.href = ck.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const lookup = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.get(`/gift/lookup/${encodeURIComponent(code.trim().toUpperCase())}`);
      setResult(data);
    } catch (e) {
      setResult({ error: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="gift-page" className="pt-32 pb-24 fade-in bg-[#F2E9D8]">
      <div className="max-w-5xl mx-auto px-6 sm:px-12 lg:px-16">
        <p className="text-xs tracking-[0.3em] uppercase font-bold">For someone special</p>
        <h1 className="font-serif font-black text-6xl sm:text-7xl text-[#121212] leading-[0.95] mt-4">
          Gift a <span className="lime-swipe italic">class.</span>
        </h1>
        <p className="mt-6 text-lg max-w-2xl text-[#121212]/80">
          The best kind of gift — a few hours in the kitchen with us. Send a code,
          they pick the class.
        </p>

        <div className="mt-12 flex gap-2 border-b-2 border-[#121212]">
          {[{k: "buy", l: "Buy a gift"}, {k: "redeem", l: "Check balance"}].map((t) => (
            <button
              key={t.k}
              data-testid={`gift-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-6 py-3 text-xs tracking-[0.2em] uppercase font-bold ${tab === t.k ? "bg-[#121212] text-[#F2E9D8]" : "text-[#121212]"}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "buy" ? (
          <div className="mt-10 grid md:grid-cols-2 gap-10">
            <div className="bg-[#121212] text-[#F2E9D8] p-10 relative aspect-[4/3] flex flex-col justify-between">
              <Star className="absolute top-6 right-6 text-[#C6FF33]" size={36} fill="#C6FF33" />
              <div>
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Gift Voucher</p>
                <p className="font-script text-5xl mt-4">Cake</p>
                <p className="font-serif font-black text-3xl">SOCIETY</p>
              </div>
              <div>
                <p className="font-serif font-black text-6xl">
                  ${customAmount || amount}
                </p>
                <p className="text-xs tracking-[0.2em] uppercase mt-2">For {recipientName || "a lucky baker"}</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Amount</label>
                <div className="grid grid-cols-4 gap-2">
                  {PRESETS.map((a) => (
                    <button
                      key={a}
                      data-testid={`amount-${a}`}
                      onClick={() => { setAmount(a); setCustomAmount(""); }}
                      className={`py-3 border-2 border-[#121212] font-bold text-sm ${amount === a && !customAmount ? "bg-[#C6FF33]" : "bg-white"}`}
                    >
                      ${a}
                    </button>
                  ))}
                </div>
                <input
                  data-testid="custom-amount"
                  type="number" placeholder="Or custom amount ($)" value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="mt-2 w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Recipient name</label>
                <input
                  data-testid="recipient-name" value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Recipient email</label>
                <input
                  data-testid="recipient-email" type="email" value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Personal message (optional)</label>
                <textarea
                  data-testid="gift-message" value={message} rows={3}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none resize-none"
                />
              </div>
              <button
                data-testid="buy-gift-btn"
                onClick={buy} disabled={busy}
                className="w-full px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center justify-center gap-3 transition-all"
              >
                <GiftIcon size={16} /> {busy ? "Loading…" : "Continue to checkout"}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-10 max-w-lg">
            <p className="text-[#121212]/80">
              Enter your gift card code to see your remaining balance. Use it at checkout on classes, camps, tutorials and the shop.
            </p>
            <div className="mt-6 flex gap-2">
              <input
                data-testid="gift-code-input" value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="GIFT-XXXX-XXXX"
                className="flex-1 px-4 py-3 border-2 border-[#121212] bg-white focus:outline-none font-mono tracking-wider uppercase"
              />
              <button
                data-testid="gift-lookup-btn"
                onClick={lookup} disabled={busy || !code}
                className="px-6 py-3 bg-[#121212] text-[#F2E9D8] text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40"
              >
                Check
              </button>
            </div>
            {result && (result.error ? (
              <p data-testid="gift-balance-error" className="mt-6 px-4 py-3 border-2 border-[#E0218A] bg-white text-[#E0218A] font-bold text-sm">{result.error}</p>
            ) : (
              <div data-testid="gift-balance-result" className="mt-6 border-2 border-[#121212] bg-white">
                <div className="p-6 bg-[#121212] text-[#F2E9D8]">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#C6FF33] font-bold">Remaining balance</p>
                  <p data-testid="gift-balance-amount" className="font-serif font-black text-6xl mt-1">${result.balance.toFixed(2)}</p>
                </div>
                <div className="p-4 flex justify-between text-sm text-[#121212]">
                  <span className="font-mono">{result.code}</span>
                  <span data-testid="gift-original-amount">Original value ${result.amount.toFixed(2)}</span>
                </div>
                {result.balance <= 0 && <p className="px-4 pb-4 text-xs text-stone-500">This gift card has been fully used.</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
